import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import Loader from '../Shared/Loader/Loader';
import APIConstant from '../../utils/pathConstants';
import api from '../../utils/apiClient';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const MarkMyAttendance = () => {
  const { catchResponse } = useCatchResponse();
  const [teacherId, setTeacherId] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [financialYear, setFinancialYear] = useState('');
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [markingSlot, setMarkingSlot] = useState(null);

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    const tid = loginData?._id;
    const sid = CommonConfig.isOrgOwner(loginData?.UserRole) ? loginData?._id : loginData?.CreatedBy;
    const orgId = loginData?.OrganizationId || '';
    const fy = CommonConfig.getCookie('selectedAcademicYear') || loginData?.FinancialYearId || '';
    setTeacherId(tid);
    setSchoolId(sid);
    setOrganizationId(orgId);
    setFinancialYear(fy);
    fetchStatus(tid, sid, orgId, fy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = async (tid, sid, orgId, fy) => {
    try {
      Loader.show();
      const res = await api.post(APIConstant.path.getMyTodayGpsStatus, {
        TeacherId: tid,
        SchoolId: sid,
        OrganizationId: orgId,
        FinancialYear: fy,
      });
      if (res.success) setStatus(res.data);
      setLoaded(true);
      Loader.hide();
    } catch (err) {
      setLoaded(true);
      Loader.hide();
      catchResponse(err);
    }
  };

  const handleMark = (slotName) => {
    if (!navigator.geolocation) {
      Toast.error({ message: 'Geolocation is not supported by your browser' });
      return;
    }
    setMarkingSlot(slotName);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await api.post(APIConstant.path.markStaffAttendanceByGps, {
            TeacherId: teacherId,
            Lat: pos.coords.latitude,
            Lng: pos.coords.longitude,
            FinancialYear: financialYear,
            SchoolId: schoolId,
            OrganizationId: organizationId,
            CreatedBy: teacherId,
            SlotName: slotName,
          });
          if (res.success) {
            Toast.success({ message: res.message });
            fetchStatus(teacherId, schoolId, organizationId, financialYear);
          } else {
            Toast.error({ message: res.message });
          }
        } catch (err) {
          catchResponse(err);
        } finally {
          setMarkingSlot(null);
        }
      },
      (err) => {
        setMarkingSlot(null);
        Toast.error({ message: `Location access denied: ${err.message}` });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const alreadyMarked = !!status?.AlreadyMarked;
  const isFullyPresent = !!status?.IsFullyPresent;
  const enabled = !!status?.IsGpsAttendanceEnabled;
  const fences = status?.ApplicableFences || [];
  const slots = status?.Slots || [];
  const markedSlotNames = status?.MarkedSlots || [];
  const nextOpenSlot = status?.NextOpenSlot || null;
  const hasOpenSlot = slots.some((s) => s.IsOpenNow && !markedSlotNames.includes(s.Name));
  const today = new Date();

  return (
    <div className="wayment-owner-dashboard">
      <div className="heading-area p-d-flex p-ai-center p-jc-between">
        <div className="p-d-flex p-ai-baseline">
          <i className="pi pi-map-marker"></i>
          <h2>Mark My Attendance</h2>
        </div>
      </div>

      {loaded && !enabled && (
        <div className="staff-summary-cards p-py-3">
          <div className="summary-card absent" style={{ flex: 1 }}>
            <div className="summary-icon">
              <i className="pi pi-info-circle"></i>
            </div>
            <div className="summary-info">
              <h4>GPS Attendance Disabled</h4>
              <h2 style={{ fontSize: '1rem', fontWeight: 500 }}>Please contact your administrator</h2>
            </div>
          </div>
        </div>
      )}

      {enabled && (
        <>
          <div className="staff-summary-cards p-py-3">
            <div className="summary-card total">
              <div className="summary-icon">
                <i className="pi pi-calendar"></i>
              </div>
              <div className="summary-info">
                <h4>Today</h4>
                <h2 style={{ fontSize: '1.1rem' }}>{today.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</h2>
              </div>
            </div>
            <div className={`summary-card ${isFullyPresent ? 'present' : 'late'}`}>
              <div className="summary-icon">
                <i className={`pi ${isFullyPresent ? 'pi-check-circle' : 'pi-clock'}`}></i>
              </div>
              <div className="summary-info">
                <h4>Status</h4>
                <h2 style={{ fontSize: '1.1rem' }}>
                  {isFullyPresent
                    ? 'Present'
                    : slots.length > 0
                      ? `${slots.filter((s) => markedSlotNames.includes(s.Name)).length}/${slots.length} marked`
                      : alreadyMarked ? 'Marked' : 'Not marked yet'}
                </h2>
              </div>
            </div>
          </div>

          {fences.length === 0 && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fdecea', color: '#c00', marginTop: 8 }}>
              <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }}></i>
              No geofence is configured for you. Contact your administrator.
            </div>
          )}
          {fences.length > 0 && slots.length === 0 && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fdecea', color: '#c00', marginTop: 8 }}>
              <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }}></i>
              No attendance type is configured for your geofence. Contact your administrator.
            </div>
          )}
          {slots.length > 0 && !hasOpenSlot && nextOpenSlot && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fff6e5', color: '#c67c00', marginTop: 8 }}>
              <i className="pi pi-clock" style={{ marginRight: 6 }}></i>
              Next window: <strong>{nextOpenSlot.Name}</strong> opens at {nextOpenSlot.WindowStart}.
            </div>
          )}
          {slots.length > 0 && !hasOpenSlot && !nextOpenSlot && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fdecea', color: '#c00', marginTop: 8 }}>
              <i className="pi pi-clock" style={{ marginRight: 6 }}></i>
              No attendance window is open right now.
            </div>
          )}

          <div className="p-grid p-mt-2">
            {slots.length > 0 && (
              <div className="p-col-12">
                <div style={{ padding: 20, borderRadius: 10, background: 'var(--surface-card, #fff)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ margin: '0 0 12px 0' }}>
                    <i className="pi pi-clock" style={{ marginRight: 6 }}></i>
                    Attendance Slots{status?.AttendanceTypeName ? ` — ${status.AttendanceTypeName}` : ''}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {slots.map((s, idx) => {
                      const isMarked = markedSlotNames.includes(s.Name);
                      const chipStyle = {
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 12,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: isMarked ? '#d4edda' : s.IsOpenNow ? '#cfe8ff' : '#f0f0f0',
                        color: isMarked ? '#155724' : s.IsOpenNow ? '#004085' : '#666',
                      };
                      const label = isMarked ? 'Marked' : `Opens ${s.WindowStart}`;
                      const canMark = s.IsOpenNow && !isMarked;
                      const isMarkingThis = markingSlot === s.Name;
                      const logs = s.Logs || [];
                      return (
                        <div key={idx} style={{ padding: '10px 12px', background: '#fafafa', borderRadius: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {s.Name}
                                {!s.MarkAttendance && (
                                  <small style={{ color: '#888', fontWeight: 400, fontStyle: 'italic', marginLeft: 6 }}>Optional</small>
                                )}
                              </div>
                              <small style={{ color: '#666' }}>Window {s.WindowStart} – {s.WindowEnd}</small>
                            </div>
                            {canMark ? (
                              <Button
                                label={isMarkingThis ? 'Locating...' : 'Mark'}
                                icon="pi pi-map-marker"
                                className="btn-text-action-primary p-button-sm"
                                onClick={() => handleMark(s.Name)}
                                disabled={!!markingSlot}
                              />
                            ) : (
                              <span style={chipStyle}>{label}</span>
                            )}
                          </div>

                          {logs.length > 0 && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                              {logs.map((log, li) => (
                                <div key={li} style={{ marginBottom: li === logs.length - 1 ? 0 : 8 }}>
                                  <div style={{ color: '#155724', fontSize: '0.78rem', fontWeight: 600 }}>
                                    <i className="pi pi-check-circle" style={{ fontSize: '0.7rem', marginRight: 5 }}></i>
                                    Marked at {new Date(log.MarkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b', paddingLeft: 17 }}>
                                    Geofence: <strong>{log.GeofenceName || '—'}</strong>
                                    {log.ParentGeofenceName ? ` (in ${log.ParentGeofenceName})` : ''}
                                  </div>
                                  {log.Lat != null && log.Lng != null && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', paddingLeft: 17 }}>
                                      Location: {Number(log.Lat).toFixed(6)}, {Number(log.Lng).toFixed(6)}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {slots.length === 0 && fences.length > 1 && (
              <div className="p-col-12">
                <div style={{ padding: 20, borderRadius: 10, background: 'var(--surface-card, #fff)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ margin: '0 0 12px 0' }}>
                    <i className="pi pi-map-marker" style={{ marginRight: 6 }}></i>
                    Allowed Areas
                  </h3>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {fences.map((f) => (
                      <li key={f._id} style={{ marginBottom: 4 }}>{f.GeofenceName}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MarkMyAttendance;
